/**
 * Classification of a failed invoice-create command.
 *
 * `xan_create_invoice_with_payload` claims its idempotency row and writes the
 * invoice, credit applications, and initial payment in one transaction, and it
 * raises on every rejection. So a rejection the server *answered* means nothing
 * was written by that attempt — the outcome is known, and the operator may
 * correct the draft and create again. Only a failure that never got an answer
 * (transport error, timeout) leaves an unknown outcome that needs
 * reconciliation, because the command may have committed before the response
 * was lost.
 *
 * `code` is the discriminator: PostgREST raises carry the SQLSTATE (`P0001` for
 * these RAISE EXCEPTIONs) or a `PGRST*` code, while the SDK's transport and
 * timeout failures carry an empty one.
 */

/**
 * A create command the server answered with an error. `code`/`statusCode` are
 * preserved from the backend response so the caller can tell a rejection apart
 * from a lost response.
 */
export class InvoiceCreateCommandError extends Error {
  readonly code?: string;
  readonly statusCode?: number;

  constructor(message: string, options: { code?: string; statusCode?: number } = {}) {
    super(message);
    this.name = "InvoiceCreateCommandError";
    this.code = options.code;
    this.statusCode = options.statusCode;
  }
}

/**
 * The RPC refuses a concurrent caller while the owning transaction is in
 * flight. That is not a rejection of this payload: the earlier attempt is still
 * unresolved, so its outcome stays unknown.
 */
const STILL_IN_PROGRESS_RE = /still in progress/i;

function readErrorCode(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "";
  }
  const candidate = (error as { code?: unknown }).code;
  return typeof candidate === "string" ? candidate.trim() : "";
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "";
}

/** True only when the server answered, so the command certainly did not commit. */
export function isDefinitiveInvoiceCreateRejection(error: unknown): boolean {
  if (!readErrorCode(error)) {
    return false;
  }
  return !STILL_IN_PROGRESS_RE.test(readErrorMessage(error));
}
